import React from 'react';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import useGroupContext from '../../../../../contexts/group/useGroupContext';
import useOperatorContext from '../../../../../contexts/operator/useOperatorContext';
import { Item } from '../../../../../contexts/site/site-context';
import { money } from '../../../../../services/format';
import { groupLockedMessage } from '../../../../../services/groupOrder';
import { CTA } from '../../../../general/Button';
import TinyModal from '../../../../general/TinyModal';
import OrderItem from '../order/OrderItem';

type GroupBasketProps = {
    showItemModal: (item: Item, edit: boolean) => void;
    // the lead goes on to choose a time and check out
    onCheckout: () => void;
};

// What the lead has, and the guests have added, as they add it. Guests only have their own.
export const GroupBasketPanel = ({ showItemModal, onCheckout }: GroupBasketProps) => {
    const basketContext = useBasketContext();
    const groupContext = useGroupContext();
    const operatorContext = useOperatorContext();

    const host = groupContext.host ?? t`The host`;

    // the lead's own first, then each guest's together
    const items = [...basketContext.basketItems].sort((a, b) => {
        const byMine = Number(!!b.groupLine?.mine) - Number(!!a.groupLine?.mine);

        return byMine !== 0 ? byMine : (a.groupLine?.participant ?? '').localeCompare(b.groupLine?.participant ?? '');
    });

    return (
        <Panel>
            <h2 className="groupBasket__title">{groupContext.isLead ? t`Your order` : t`Your items`}</h2>

            {items.length === 0 ? (
                <div className="groupBasket__empty">
                    <span className="material-symbols-outlined">shopping_basket</span>
                    <p>{t`Your basket is empty`}</p>
                </div>
            ) : (
                <div className="groupBasket__items">
                    {items.map((item) => (
                        <OrderItem
                            key={`group-basket-item-${item.basketIndex}`}
                            item={item}
                            index={0}
                            showItemModal={() => showItemModal(item, true)}
                            unavailableScheduledItem={false}
                        />
                    ))}
                </div>
            )}

            <div className="groupBasket__total">
                <span>{t`Subtotal`}</span>
                <span>{money(basketContext.price.subtotal, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
            </div>

            {groupContext.isLead ? (
                <>
                    <CTA type="button" block disabled={items.length === 0 || basketContext.loading} onClick={onCheckout}>
                        <span>{t`Choose a time and check out`}</span>
                    </CTA>
                    <p className="groupBasket__note">{t`Guests can add items until you do.`}</p>
                </>
            ) : (
                <p className="groupBasket__note">
                    {groupContext.locked
                        ? groupLockedMessage(groupContext.host)
                        : t`You can change your items until ${host} goes to checkout. ${host} will check out and pay.`}
                </p>
            )}
        </Panel>
    );
};

// For guests on narrow screens, where there's no room beside the menu
export const GuestBasketModal = ({ show, onHide, showItemModal }: { show: boolean; onHide: () => void; showItemModal: GroupBasketProps['showItemModal'] }) => (
    <TinyModal show={show} onHide={onHide} modalID="group-guest-basket-modal" header={t`Your items`}>
        <GroupBasketPanel showItemModal={showItemModal} onCheckout={() => undefined} />
    </TinyModal>
);

const Panel = styled.div`
    background: #ffffff;
    border: 1px solid var(--border-grey);
    border-radius: var(--radius_l);
    padding: 16px;

    .groupBasket__title {
        font-size: 20px;
        line-height: 28px;
        margin: 0 0 8px;
    }

    .groupBasket__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 24px 0;
        color: var(--text-grey);

        .material-symbols-outlined {
            font-size: 40px;
            line-height: 40px;
        }

        p {
            margin: 0;
        }
    }

    .groupBasket__items {
        margin: 0 -8px;
    }

    .groupBasket__total {
        display: flex;
        justify-content: space-between;
        padding: 16px 0;
        margin-top: 8px;
        border-top: 1px solid var(--border-grey);
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;
        color: var(--text-black);
    }

    .groupBasket__note {
        margin: 12px 0 0;
        font-size: 14px;
        line-height: 20px;
        color: var(--text-grey);
        text-align: center;
    }
`;

export default GroupBasketPanel;
